import { ChangeDetectionStrategy, Component, computed, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MAX_EMAIL_LENGTH } from '@bodyanalytics/data-models-ui';

/**
 * Shape emitted to the container when the account form is submitted.
 */
export interface RegisterAccountSubmitI {
  emailAddress: string;
  password: string;
  confirmPassword: string;
  role: string;
}

/**
 * Password strength levels surfaced in the strength hint.
 */
export type PasswordStrengthLevelT = 'empty' | 'weak' | 'fair' | 'strong';

const MIN_PASSWORD_LENGTH = 6;

/**
 * Re-exported for existing consumers (#2122); the constant now lives in
 * `@bodyanalytics/data-models-ui` so sibling email forms can share it (#2129).
 */
export { MAX_EMAIL_LENGTH };

@Component({
  selector: 'bodyanalytics-register-account-form',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './account-form.component.html',
  styleUrls: ['./account-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterAccountFormComponent {
  /** Emits the new-account payload when the form is submitted. */
  submitFormEvent = output<RegisterAccountSubmitI>();

  formsubmit = false;

  /** Show/hide state for the password and confirm-password fields. */
  readonly showPassword = signal(false);
  readonly showConfirmPassword = signal(false);

  readonly securityForm = new FormGroup(
    {
      emailAddress: new FormControl('', [
        Validators.required,
        Validators.email,
        Validators.maxLength(MAX_EMAIL_LENGTH),
      ]),
      newPassword: new FormControl('', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]),
      confirmPassword: new FormControl('', [Validators.required]),
      role: new FormControl('Customer', [Validators.required]),
    },
    { validators: passwordsMatchValidator },
  );

  /** Current raw password value, mirrored into a signal for the strength hint. */
  private readonly passwordValue = signal('');

  /** Derived password-strength descriptor used to render the strength meter. */
  readonly passwordStrength = computed<{ level: PasswordStrengthLevelT; score: number }>(() => {
    const value = this.passwordValue();
    if (!value) {
      return { level: 'empty', score: 0 };
    }
    let score = 0;
    if (value.length >= MIN_PASSWORD_LENGTH) score++;
    if (value.length >= 10) score++;
    if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score++;
    if (/\d/.test(value)) score++;
    if (/[^A-Za-z0-9]/.test(value)) score++;

    if (score <= 2) return { level: 'weak', score };
    if (score <= 3) return { level: 'fair', score };
    return { level: 'strong', score };
  });

  /** Individual requirement checks rendered as a checklist under the password field. */
  readonly passwordRequirements = computed(() => {
    const value = this.passwordValue();
    return [
      { label: `At least ${MIN_PASSWORD_LENGTH} characters`, met: value.length >= MIN_PASSWORD_LENGTH },
      { label: 'Upper & lowercase letters', met: /[A-Z]/.test(value) && /[a-z]/.test(value) },
      { label: 'At least one number', met: /\d/.test(value) },
    ];
  });

  constructor() {
    this.securityForm.controls.newPassword.valueChanges.subscribe(value => {
      this.passwordValue.set(value ?? '');
    });
  }

  /** Toggle visibility of the main password field. */
  togglePassword(): void {
    this.showPassword.update(v => !v);
  }

  /** Toggle visibility of the confirm-password field. */
  toggleConfirmPassword(): void {
    this.showConfirmPassword.update(v => !v);
  }

  /** True when both passwords are filled but do not match. */
  get passwordsMismatch(): boolean {
    return !!this.securityForm.errors?.['passwordsMismatch'] && !!this.securityForm.controls.confirmPassword.value;
  }

  updateSettings(): void {
    this.formSubmit();
    if (this.securityForm.invalid) {
      this.securityForm.markAllAsTouched();
      return;
    }
    this.submitFormEvent.emit({
      emailAddress: this.securityForm.value.emailAddress as string,
      password: this.securityForm.value.newPassword as string,
      confirmPassword: this.securityForm.value.newPassword as string,
      role: 'Customer',
    });
  }

  /** Returns form controls. */
  get form() {
    return this.securityForm.controls;
  }

  /** Marks the form as submitted (drives validation tooltips). */
  formSubmit(): void {
    this.formsubmit = true;
  }
}

/**
 * Cross-field validator ensuring password and confirm-password match.
 */
export function passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
  const password = group.get('newPassword')?.value;
  const confirm = group.get('confirmPassword')?.value;
  if (!password || !confirm) {
    return null;
  }
  return password === confirm ? null : { passwordsMismatch: true };
}
